import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
    getLemonSqueezyAttemptStatus,
    reconcileLemonSqueezyAttempt,
} from "../../api";
import UserHeaderBar from "../../components/userHeaderBar/userHeaderBar";
import styles from "../../publicAsset/userSettingPage.module.scss";
import UserInfoHolder from "../../components/userInforHolder/userInforHolder";
import UserInvitationHolder from "../../components/userInvitationHolder/userInvitationHolder";
import UserBillingHolder from "../../components/userBillingHolder/userBIllingHolder";
import PaymentSuccessModal from "../../components/paymentSuccessModal/PaymentSuccessModal";
import { isFeatureEnabled, FEATURES } from "../../utils/featureFlags";
import messageQueue from "../../utils/messageQueue";

const UserSetting = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [showPaymentSuccessModal, setShowPaymentSuccessModal] = useState(false);
    const [paymentSuccessData, setPaymentSuccessData] = useState(null);
    const [isConfirmingPayment, setIsConfirmingPayment] = useState(false);

    const hasPaymentSuccessFlag = useMemo(() => {
        const params = new URLSearchParams(location.search);
        return params.get('payment') === 'success';
    }, [location.search]);

    useEffect(() => {
        if (!hasPaymentSuccessFlag) {
            return;
        }

        let isCancelled = false;
        const params = new URLSearchParams(location.search);
        const attemptId = params.get('attemptId') || null;
        const orderId = params.get('order_id') || params.get('orderId') || params.get('_ptxn') || null;
        const packageId = params.get('packageId') || null;

        const fallbackPaymentData = {
            transactionId: orderId,
            packageId: params.get('packageId') || null,
            packageName: params.get('packageName') || null,
            completedAt: new Date().toISOString(),
        };

        const confirmPayment = async () => {
            if (!packageId) {
                messageQueue.warning(
                    'PAYMENT_CONFIRMATION_PENDING',
                    'Payment redirect detected, but confirmation is still pending. Please refresh in a moment.'
                );
                return;
            }

            if (!attemptId) {
                messageQueue.warning(
                    'PAYMENT_CONFIRMATION_PENDING',
                    'Payment redirect detected, but attempt ID is missing. Please retry from checkout.'
                );
                return;
            }

            setIsConfirmingPayment(true);

            try {
                const maxAttempts = 20;
                const pollDelayMs = 3000;

                for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
                    if (isCancelled) {
                        return;
                    }

                    const response = await getLemonSqueezyAttemptStatus(attemptId);
                    const status = response?.data?.status;
                    const confirmedPackageId = response?.data?.packageId;

                    if (status === 'confirmed' && String(confirmedPackageId || '') === String(packageId)) {
                        setPaymentSuccessData(fallbackPaymentData);
                        setShowPaymentSuccessModal(true);
                        return;
                    }

                    if (status === 'failed' || status === 'expired') {
                        messageQueue.error(
                            'PAYMENT_CONFIRMATION_FAILED',
                            response?.data?.message || 'Payment could not be confirmed. Please contact support.'
                        );
                        return;
                    }

                    if (attempt < maxAttempts) {
                        // Wait before checking if webhook processing has updated user subscription state.
                        await new Promise((resolve) => setTimeout(resolve, pollDelayMs));
                    }
                }

                const reconcileResponse = await reconcileLemonSqueezyAttempt(attemptId);
                const reconcileStatus = reconcileResponse?.data?.status;

                if (reconcileStatus === 'confirmed') {
                    setPaymentSuccessData(fallbackPaymentData);
                    setShowPaymentSuccessModal(true);
                    return;
                }

                if (!isCancelled) {
                    messageQueue.warning(
                        'PAYMENT_CONFIRMATION_PENDING',
                        'Payment completed, but subscription activation is still pending confirmation. Please check again shortly.'
                    );
                }
            } catch (error) {
                if (!isCancelled) {
                    messageQueue.warning(
                        'PAYMENT_CONFIRMATION_PENDING',
                        'Payment received, but confirmation is still pending. Please check again shortly.'
                    );
                }
            } finally {
                if (!isCancelled) {
                    setIsConfirmingPayment(false);
                }
            }
        };

        confirmPayment();

        return () => {
            isCancelled = true;
        };
    }, [hasPaymentSuccessFlag, location.search]);

    const handleClosePaymentSuccessModal = () => {
        setShowPaymentSuccessModal(false);
        setPaymentSuccessData(null);

        if (hasPaymentSuccessFlag) {
            navigate('/user-setting', { replace: true });
        }
    };

    return (
        <>
            <Helmet>
                <title>User Settings | Prepal</title>
                <meta name="description" content="User Settings for Prepal account holders." />
                <meta name="keywords" content="Prepal, user settings, education" />
                <meta name="author" content="Prepal Team" />
            </Helmet>
            <div className={styles.userSettingPage}>
                <PaymentSuccessModal
                    open={showPaymentSuccessModal && !isConfirmingPayment}
                    packageName={paymentSuccessData?.packageName}
                    onClose={handleClosePaymentSuccessModal}
                />
                <UserHeaderBar />
                <div className={styles.userInfoHolder}>
                    <UserInfoHolder />
                </div>
                <div className={styles.userBillingHolderAndInvitationHolder}>
                    {isFeatureEnabled(FEATURES.INVITATION) && <UserInvitationHolder />}
                    <UserBillingHolder />
                </div>
            </div>
        </>
    );
};

export default UserSetting;
